/**
 * Normalisers shared by the router's providers. They emit exactly the shapes the existing
 * providers already produce (StarAPI.processResponse): InstagramUserQueryProps rows for a user
 * search, and `{ graphql: { user } }` for a profile — so cached data and callers don't change.
 *
 * Pure, dependency-free, edge-safe.
 */

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** One raw IG user (private-API or web shape, optionally wrapped in `{ user }`) → search row, or null. */
export function toIgUserRow(entry: any): Record<string, unknown> | null {
	const user = entry?.user ?? entry;
	const username = typeof user?.username === 'string' ? user.username : '';
	if (!username) return null;
	const pic = user.profile_pic_url ?? user.profile_pic_url_hd ?? user.avatar_url ?? '';
	return {
		full_name: user.full_name ?? '',
		avatar_url: pic,
		profile_pic_url: pic,
		username,
		is_verified: !!user.is_verified,
		is_private: !!user.is_private,
		link: `instagram/${username}/`,
		type: 'ig',
		user_id: String(user.pk ?? user.pk_id ?? user.id ?? ''),
		follower_count: 0,
	};
}

/** Search body (top-level array, or `{ users: [...] }`) → rows. An empty list is a valid answer. */
export function parseIgUserSearch(body: unknown, limit: number): Record<string, unknown>[] {
	const b: any = body;
	const list: unknown = Array.isArray(b) ? b : Array.isArray(b?.users) ? b.users : Array.isArray(b?.data) ? b.data : null;
	if (!list) throw new Error('unexpected search body');
	const rows: Record<string, unknown>[] = [];
	for (const entry of list as unknown[]) {
		const row = toIgUserRow(entry);
		if (row) rows.push(row);
		if (rows.length >= limit) break;
	}
	return rows;
}

/** Any of the IG profile shapes seen in the wild → `{ graphql: { user } }` (profile only, no posts). */
export function toIgProfile(raw: any): { graphql: { user: Record<string, unknown> } } {
	const user = raw?.data?.user ?? raw?.user ?? raw;
	const id = user?.id ?? user?.pk ?? user?.pk_id;
	if (!user || typeof user.username !== 'string' || !id) throw new Error('malformed profile: no user / username / id');
	const followers = num(user.follower_count) ?? num(user.edge_followed_by?.count);
	const following = num(user.following_count) ?? num(user.edge_follow?.count);
	const media = num(user.media_count) ?? num(user.edge_owner_to_timeline_media?.count);
	const pic = user.profile_pic_url ?? '';
	return {
		graphql: {
			user: {
				id: String(id),
				username: user.username,
				full_name: user.full_name,
				biography: user.biography,
				category: user.category_name || user.category || null,
				external_url: user.external_url || null,
				external_url_title: user.bio_links?.[0]?.title || null,
				profile_pic_url: pic,
				profile_pic_url_hd: user.profile_pic_url_hd || user.hd_profile_pic_url_info?.url || pic,
				follower_count: followers,
				following_count: following,
				media_count: media,
				is_private: user.is_private,
				is_verified: user.is_verified,
				is_business: user.is_business_account ?? user.is_business,
				is_professional: user.is_professional_account ?? user.is_business,
				edge_followed_by: { count: followers },
				edge_follow: { count: following },
			},
		},
	};
}

/** Browser-like headers the own-site / anti-bot lanes need (a Chrome desktop fingerprint). */
export const CHROME_UA =
	'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36';
export function browserHeaders(extra: Record<string, string> = {}): Record<string, string> {
	return {
		'User-Agent': CHROME_UA,
		Accept: 'application/json, text/plain, */*',
		'Accept-Language': 'en-US,en;q=0.9',
		'sec-ch-ua': '"Chromium";v="130", "Google Chrome";v="130", "Not?A_Brand";v="99"',
		'sec-ch-ua-mobile': '?0',
		'sec-ch-ua-platform': '"macOS"',
		...extra,
	};
}

// ── validated success (plan §3.6): a 2xx is not an answer until it passes this ────────────────

export type Verdict = 'ok' | 'empty' | 'invalid';

const squash = (v: unknown): string => String(v ?? '').toLowerCase().replace(/^[@#]/, '').replace(/[\s._\-]/g, '');

/** ok = usable; empty = a valid "no results" (search) ; invalid = a 2xx that is not what the capability promises. */
export function validateResult(
	req: { capability: string; query: string; platform?: string },
	value: unknown
): Verdict {
	const v: any = value;
	if (req.capability === 'profile' && req.platform === 'tiktok') {
		// TikTok lanes return a flat TiktokProfileProps (not graphql.user); `false` is its "no such user".
		if (v === false) return 'empty';
		const handle = String(req.query ?? '').trim().replace(/^@/, '').replace(/\s+/g, '').toLowerCase();
		const isCount = (n: unknown) => typeof n === 'number' && Number.isFinite(n);
		return v && typeof v === 'object' && v.id && typeof v.username === 'string' && v.username.replace(/\s+/g, '').toLowerCase() === handle && isCount(v.follower_count) && isCount(v.following_count) ? 'ok' : 'invalid';
	}
	if (req.capability === 'search_users') {
		if (!Array.isArray(v)) return 'invalid';
		if (v.length === 0) return 'empty';
		const rows = v.filter((r) => r && typeof r === 'object' && (typeof r.username === 'string' || typeof r.unique_id === 'string'));
		if (!rows.length) return 'invalid';
		// plausibility, not strictness: search is fuzzy (full names, near matches), so only the first
		// three characters of the query must appear in some row's handle or display name.
		const needle = squash(req.query).slice(0, 3);
		if (!needle) return 'ok';
		return rows.some((r) => squash(r.username ?? r.unique_id).includes(needle) || squash(r.full_name ?? r.nickname).includes(needle)) ? 'ok' : 'invalid';
	}
	if (req.capability === 'profile') {
		const user = v?.graphql?.user;
		// exact handle (case-folded, leading @ dropped — NO dot/underscore squashing: john.doe and johndoe are different
		// accounts) and the three counts every real profile carries
		const handle = String(req.query ?? '').trim().replace(/^@/, '').toLowerCase();
		const counts = [user?.follower_count, user?.following_count, user?.media_count];
		return typeof user?.username === 'string' && user.username.toLowerCase() === handle && counts.every((n) => typeof n === 'number' && Number.isFinite(n)) ? 'ok' : 'invalid';
	}
	if (req.capability === 'exists') return v?.exists === true ? 'ok' : 'invalid';
	return 'invalid';
}
